import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useErp } from "@/context/ErpContext";
import {
  ChartLine, Vault, HandCoins, ListBullets, CurrencyCircleDollar,
  CalendarCheck,
  Bank, ArrowsClockwise, SignOut, ShieldCheck, PlugsConnected,
  Certificate, Scroll, Sparkle, CaretDown, List, X,
  FileText, UserPlus, Clock, Users, BookOpen, Receipt,
  UserCircle, MagnifyingGlass, PlusCircle,
  ClipboardText, ShoppingCart, Megaphone, ArrowsLeftRight,
  Wallet, Package, MapPin, ListChecks,
  Cube, Buildings, Truck, ClipboardText as ClipboardIcon,
  Wrench, ArrowsOut, Coins, Archive,
  Gear,
  Brain,
  Database,
  MapTrifold,
  Broadcast,
  Lightning,
  Warning,
  EnvelopeSimple,
  Sun,
  Moon,
  FilePlus,GearSix
} from "@phosphor-icons/react";
import ProfileSetupModal from "@/components/erp/ProfileSetupModal";
import AgentRunModal from "@/components/erp/AgentModalRouter";
import PurchaseReqAgentModal from "@/components/erp/PurchaseReqAgentModal";
import PurchaseOrderAgentModal from "@/components/erp/PurchaseOrderAgentModal";

// Apply persisted theme as early as possible so we avoid a light-flash
// on first paint after a hard reload.
const applyStoredTheme = () => {
  try {
    const v = localStorage.getItem("theme");
    document.documentElement.classList.toggle("dark", v === "dark");
  } catch { /* ignore */ }
};
applyStoredTheme();

function useTheme() {
  const [theme, setTheme] = React.useState(() => {
    try { return localStorage.getItem("theme") === "dark" ? "dark" : "light"; } catch { return "light"; }
  });

  React.useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    try { localStorage.setItem("theme", theme); } catch { /* ignore */ }
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));
  // Forces theme back to light — used on sign-out so a logged-out / freshly
  // logged-in session never inherits a stale dark preference.
  const forceLight = () => setTheme("light");

  return [theme, toggleTheme, forceLight];
}

const NAV = [
  { to: "/dashboard",        label: "Dashboard",        icon: ChartLine,           testid: "nav-dashboard" },
  { to: "/fixed-deposits",   label: "Fixed Deposits",   icon: Vault,               testid: "nav-fds" },
  { to: "/loans",            label: "Loans",            icon: HandCoins,           testid: "nav-loans" },
  { to: "/bank-guarantees",  label: "Bank Guarantees",  icon: Certificate,         testid: "nav-bg" },
  { to: "/letters-of-credit",label: "Letters of Credit",icon: Scroll,              testid: "nav-lc" },
  { to: "/transactions",     label: "Transactions",     icon: ListBullets,         testid: "nav-txns" },
  { to: "/fx",               label: "FX & MTM",         icon: CurrencyCircleDollar,testid: "nav-fx" },
  { to: "/bank-accounts",    label: "Bank Accounts",    icon: Bank,                testid: "nav-banks" },
  { to: "/reconciliation",   label: "Reconciliation",   icon: ArrowsClockwise,     testid: "nav-reco" },
  { to: "/approvals",        label: "Approvals",        icon: ShieldCheck,         testid: "nav-approvals" },
];

const ALM_NAV = [
  { to: "/alm/dashboard",          label: "ALM Dashboard",       icon: ChartLine,    testid: "nav-alm-dashboard" },
  { to: "/alm/map",                label: "Live Asset Map",      icon: MapTrifold,   testid: "nav-alm-map" },
  { to: "/alm/products",           label: "Product Master",      icon: Cube,         testid: "nav-alm-products" },
  { to: "/alm/sites",              label: "Site Master",         icon: Buildings,    testid: "nav-alm-sites" },
  { to: "/alm/locations",          label: "Location Master",     icon: MapPin,       testid: "nav-alm-locations" },
  { to: "/alm/procurement-plans",  label: "Plan to Procure",     icon: ClipboardIcon,testid: "nav-alm-pp" },
  { to: "/alm/receipts",           label: "Receiving Process",   icon: Truck,        testid: "nav-alm-receipts" },
  { to: "/alm/assets",             label: "Asset Tracking",      icon: Package,      testid: "nav-alm-assets" },
  { to: "/alm/movements",          label: "Movement of Items",   icon: ArrowsOut,    testid: "nav-alm-movements" },
  { to: "/alm/capitalizations",    label: "Asset Capitalization",icon: Coins,        testid: "nav-alm-cap" },
  { to: "/alm/maintenance",        label: "Asset Maintenance",   icon: Wrench,       testid: "nav-alm-maint" },
  { to: "/alm/retirements",        label: "Asset Retirement",    icon: Archive,      testid: "nav-alm-ret" },
];

const AGENT_ICONS = {
  "invoice-create":          FileText,
  "supplier-create":         UserPlus,
  "invoice-ageing":          Clock,
  "supplier-ageing":         Users,
  "journal-create":          BookOpen,
  "receipt-create":          Receipt,
  "customer-create":         UserCircle,
  "get-hr":                  MagnifyingGlass,
  "create-hr":               PlusCircle,
  "employee-leave-application": CalendarCheck,
  "purchase-req-create":     ClipboardText,
  "purchase-order-create":   ShoppingCart,
  "rfp-create":              Megaphone,
  "move-order-create":       FilePlus,
  "transact-move-order":     ArrowsLeftRight,
  "manufacturing-work-order": GearSix,
  "maintenance-work-order" : Wrench,
  "bank-transfer":           ArrowsLeftRight,
  "bank-balance":            Wallet,
  "asset-addition":          Package,
  "asset-transfer":          MapPin,
  "asset-retirement":        ListChecks,
  "sales-order-booking":     ShoppingCart,
  "item-creation":           ListBullets,
  // Finance Efficiency agents
  "finance-account-overview":BookOpen,
  "finance-anomaly":         MagnifyingGlass,
  "finance-variance":        ChartLine,
  "finance-future-insight":  Sparkle,
  "finance-narrative":       FileText,
  "finance-monthend":        ListChecks,
};

// ── Keys that belong to the Finance Efficiency section ────────────────────────
const FINANCE_EFFICIENCY_KEYS = new Set([
  "finance-account-overview",
  "finance-anomaly",
  "finance-variance",
  "finance-future-insight",
  "finance-narrative",
  "finance-monthend",
]);

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { launchAgent, agents, setSetupOpen ,profile  } = useErp();
  const nav = useNavigate();
  const [theme, toggleTheme, forceLight] = useTheme();

  const [erpOpen,      setErpOpen]      = React.useState(true);
  const [finOpen,      setFinOpen]      = React.useState(true);
  const [almOpen,      setAlmOpen]      = React.useState(true);
  const [treasuryOpen, setTreasuryOpen] = React.useState(true);
  const [adminOpen,    setAdminOpen]    = React.useState(true);
  const [autoOpen,     setAutoOpen]     = React.useState(true);
  const [mobileOpen,   setMobileOpen]   = React.useState(false);

  const closeMobile = () => setMobileOpen(false);

  // ── Normalise the agent list ───────────────────────────────────────────────
  // ErpContext may deliver agents as:
  //   • objects with { key, label, category, hint }  (full catalog)
  //   • objects with { key, label }                  (no category field)
  //   • plain strings like "invoice-create"          (legacy / auth-me response)
  //
  // We normalise everything into { key, label, category } so the rest of the
  // component doesn't have to worry about the shape.
  const normalisedAgents = React.useMemo(() => {
    if (!Array.isArray(agents)) return [];
    return agents.map((a) => {
      if (typeof a === "string") {
        // plain key string — give it a sensible label and derive category
        return {
          key: a,
          label: a.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
          category: FINANCE_EFFICIENCY_KEYS.has(a) ? "finance_efficiency" : "core",
        };
      }
      // object — honour existing category or derive it from the key
      return {
        ...a,
        category:
          a.category ||
          (FINANCE_EFFICIENCY_KEYS.has(a.key) ? "finance_efficiency" : "core"),
      };
    });
  }, [agents]);

  const coreAgents    = normalisedAgents.filter((a) => a.category === "core");
  const financeAgents = normalisedAgents.filter((a) => a.category === "finance_efficiency");

  // ── Admin / role checks ───────────────────────────────────────────────────
  const isAdmin = !!(
    user?.is_admin ||
    user?.role === "admin" ||
    user?.role === "product_admin"
  );

  // Menu-visibility gate.
  // If the backend doesn't return allowed_menus we default to showing everything.
  const ALL_MENUS = ["admin", "erp_agents", "finance_efficiency", "telecom", "treasury"];
  const allowedMenus =
    Array.isArray(user?.allowed_menus) && user.allowed_menus.length > 0
      ? user.allowed_menus
      : ALL_MENUS;

  const canSee = (k) => {
    if (user?.is_admin) return true; // real admins see everything
    if (isAdmin) return true;        // product_admin / role=admin also see everything
    return allowedMenus.includes(k);
  };

  // ── Nav item class ────────────────────────────────────────────────────────
  const navItemClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium rounded-md transition-colors mx-2 ${
      isActive
        ? "bg-[#2563EB] text-white"
        : "text-gray-300 hover:bg-white/[0.06] hover:text-white"
    }`;

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-[#1F2A40] bg-[#1A2238] px-4 h-14 text-white">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="app-mobile-brand">
          <img src="/vaku-logo.png" alt="Vaku" className="h-11 w-11 rounded-lg object-cover" />
          <span className="font-black text-base tracking-tight text-white">Vaku</span>
        </Link>
        <button onClick={() => setMobileOpen(true)} data-testid="app-mobile-menu-toggle" aria-label="Open menu" className="text-gray-200">
          <List size={24} weight="bold" />
        </button>
      </header>

      {/* Backdrop (mobile only) */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden transition-opacity"
          onClick={closeMobile}
          data-testid="app-sidebar-backdrop"
        />
      )}

      <div className="lg:flex">
        {/* ── Sidebar ── */}
        <aside
          data-testid="app-sidebar"
          className={`fixed lg:sticky top-0 inset-y-0 left-0 z-50 w-72 max-w-[85vw] lg:w-56 shrink-0 h-screen bg-[#1A2238] text-gray-200 flex flex-col transition-transform duration-300 ease-in-out lg:translate-x-0 ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          {/* Brand */}
           <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="brand-link" onClick={closeMobile}>
              <img src="/vaku-logo.png" alt="Vaku" className="h-10 w-10 rounded-lg object-cover" />
              <div>
                <div className="text-[18px] font-bold tracking-tight text-white leading-none">Vaku</div>
                <div className="text-[9px] tracking-[0.18em] uppercase text-gray-400 mt-1">the only AI</div>
              </div>
            </Link>
            <button onClick={closeMobile} className="lg:hidden text-gray-300 hover:text-white p-1" data-testid="app-sidebar-close" aria-label="Close menu">
              <X size={22} weight="bold" />
            </button>
          </div>

          <nav className="sidebar-scroll flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">

            {/* 0) Admin Console — admin / product_admin only */}
            {isAdmin && canSee("admin") && (
              <div className="pb-3 mb-2 border-b border-white/10">
                <button
                  onClick={() => setAdminOpen((o) => !o)}
                  data-testid="admin-group-toggle"
                  className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
                >
                  <span className="flex items-center gap-2">
                    <Gear size={14} weight="fill" className="text-amber-400" /> Admin Console
                  </span>
                  <CaretDown size={12} weight="bold" className={`transition-transform ${adminOpen ? "" : "-rotate-90"}`} />
                </button>
                {adminOpen && (
                  <div className="mt-1 space-y-0.5" data-testid="admin-group">
                    <NavLink to="/admin/agent-setup" data-testid="nav-admin-agent-setup" className={navItemClass} onClick={closeMobile}>
                      <Sparkle size={16} weight="bold" />
                      <span>Agent Setup</span>
                    </NavLink>
                    <NavLink to="/admin/users" data-testid="nav-admin-users" className={navItemClass} onClick={closeMobile}>
                      <Users size={16} weight="bold" />
                      <span>Users & Access</span>
                    </NavLink>
                    <NavLink to="/admin/llm-settings" data-testid="nav-admin-llm-settings" className={navItemClass} onClick={closeMobile}>
                      <Brain size={16} weight="bold" />
                      <span>LLM Settings</span>
                    </NavLink>
                    {canSee("vakuai_db") && (
                      <NavLink to="/admin/database" data-testid="nav-admin-database" className={navItemClass} onClick={closeMobile}>
                        <Database size={16} weight="bold" />
                        <span>Vakuai Database</span>
                      </NavLink>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 0.5) Automations — top-level group with sub-tabs (below Admin Console) */}
            {canSee("finance_efficiency") && (
              <div className="pb-3 mb-2 border-b border-white/10">
                <button
                  onClick={() => setAutoOpen((o) => !o)}
                  data-testid="automations-group-toggle"
                  className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
                >
                  <span className="flex items-center gap-2">
                    <ArrowsClockwise size={14} weight="fill" className="text-amber-400" /> Automations
                  </span>
                  <CaretDown size={12} weight="bold" className={`transition-transform ${autoOpen ? "" : "-rotate-90"}`} />
                </button>
                {autoOpen && (
                  <div className="mt-1 space-y-0.5" data-testid="automations-group">
                    <NavLink to="/email-agent" data-testid="nav-email-agent" className={navItemClass} onClick={closeMobile}>
                      <EnvelopeSimple size={16} weight="bold" />
                      <span>Email Agent</span>
                    </NavLink>
                    <NavLink to="/automations" data-testid="nav-automations" className={navItemClass} onClick={closeMobile}>
                      <ArrowsClockwise size={16} weight="bold" />
                      <span>Overview</span>
                    </NavLink>
                  </div>
                )}
              </div>
            )}

            {/* 1) ERP AI Agents */}
            {canSee("erp_agents") && (
              <div>
                <button
                  onClick={() => setErpOpen((o) => !o)}
                  data-testid="erp-agents-group-toggle"
                  className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
                >
                  <span className="flex items-center gap-2">
                    <Sparkle size={14} weight="fill" className="text-amber-400" /> ERP AI Agents
                  </span>
                  <CaretDown size={12} weight="bold" className={`transition-transform ${erpOpen ? "" : "-rotate-90"}`} />
                </button>
                {erpOpen && (
                  <div className="mt-1 space-y-0.5" data-testid="erp-agents-group">
                    {coreAgents.length === 0 ? (
                      <div className="px-3 py-2 text-[11px] text-gray-500 italic">No agents assigned</div>
                    ) : (
                      coreAgents.map(({ key, label }) => {
                        const Icon = AGENT_ICONS[key] || Sparkle;
                        return (
                          <button
                            key={key}
                            data-testid={`agent-${key}`}
                            onClick={() => { closeMobile(); launchAgent(key); }}
                            className="w-full flex items-center gap-3 px-3 py-2 mx-2 rounded-md text-[13px] font-medium text-gray-300 hover:bg-white/[0.06] hover:text-white transition-colors text-left"
                            title={label}
                          >
                            <Icon size={16} weight="bold" className="shrink-0 text-gray-400" />
                            <span className="truncate">{label}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 2) Finance Efficiency */}
            {canSee("finance_efficiency") && (
              <div className="pt-4 mt-2 border-t border-white/10">
                <button
                  onClick={() => setFinOpen((o) => !o)}
                  data-testid="finance-eff-group-toggle"
                  className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
                >
                  <span className="flex items-center gap-2">
                    <ChartLine size={14} weight="fill" className="text-amber-400" /> Finance Efficiency
                  </span>
                  <CaretDown size={12} weight="bold" className={`transition-transform ${finOpen ? "" : "-rotate-90"}`} />
                </button>
                {finOpen && (
                  <div className="mt-1 space-y-0.5" data-testid="finance-eff-group">
                    {financeAgents.length === 0 ? (
                      <div className="px-3 py-2 text-[11px] text-gray-500 italic">No agents assigned</div>
                    ) : (
                      financeAgents.map(({ key, label }) => {
                        const Icon = AGENT_ICONS[key] || Sparkle;
                        return (
                          <button
                            key={key}
                            data-testid={`agent-${key}`}
                            onClick={() => { closeMobile(); launchAgent(key); }}
                            className="w-full flex items-center gap-3 px-3 py-2 mx-2 rounded-md text-[13px] font-medium text-gray-300 hover:bg-white/[0.06] hover:text-white transition-colors text-left"
                            title={label}
                          >
                            <Icon size={16} weight="bold" className="shrink-0 text-gray-400" />
                            <span className="truncate">{label}</span>
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 3) Telecom Solution */}
            {canSee("telecom") && (
              <div className="pt-4 mt-2 border-t border-white/10">
                <button
                  onClick={() => setAlmOpen((o) => !o)}
                  data-testid="telecom-group-toggle"
                  className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
                >
                  <span className="flex items-center gap-2">
                    <Broadcast size={14} weight="fill" className="text-amber-400" /> Telecom Solution
                  </span>
                  <CaretDown size={12} weight="bold" className={`transition-transform ${almOpen ? "" : "-rotate-90"}`} />
                </button>
                {almOpen && (
                  <div className="mt-1 space-y-0.5" data-testid="telecom-group">
                    <div className="px-3 pt-2 pb-1 text-[9px] tracking-[0.18em] uppercase font-bold text-gray-400">
                      AI Insights
                    </div>
                    <NavLink to="/telecom/finance-bss" data-testid="nav-telecom-finance-bss" className={navItemClass} onClick={closeMobile}>
                      <ChartLine size={16} weight="bold" /><span>Finance × BSS</span>
                    </NavLink>
                    <NavLink to="/telecom/revenue-assurance" data-testid="nav-telecom-ra" className={navItemClass} onClick={closeMobile}>
                      <Warning size={16} weight="bold" /><span>Revenue Assurance</span>
                    </NavLink>
                    <NavLink to="/telecom/spectrum" data-testid="nav-telecom-spectrum" className={navItemClass} onClick={closeMobile}>
                      <Broadcast size={16} weight="bold" /><span>Spectrum & Licences</span>
                    </NavLink>
                    <NavLink to="/telecom/slicing" data-testid="nav-telecom-slicing" className={navItemClass} onClick={closeMobile}>
                      <Lightning size={16} weight="bold" /><span>5G Slicing Billing</span>
                    </NavLink>
                    <div className="px-3 pt-3 pb-1 text-[9px] tracking-[0.18em] uppercase font-bold text-gray-400">
                      Asset Lifecycle (ALM)
                    </div>
                    {ALM_NAV.map(({ to, label, icon: Icon, testid }) => (
                      <NavLink key={to} to={to} data-testid={testid} className={navItemClass} onClick={closeMobile}>
                        <Icon size={16} weight="bold" />
                        <span>{label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 4) Dashboard & Treasury */}
            {canSee("treasury") && (
              <div className="pt-4 mt-2 border-t border-white/10" data-testid="treasury-group">
                <button
                  onClick={() => setTreasuryOpen((o) => !o)}
                  data-testid="treasury-group-toggle"
                  className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
                >
                  <span className="flex items-center gap-2">
                    <ChartLine size={14} weight="fill" className="text-amber-400" /> Dashboard &amp; Treasury
                  </span>
                  <CaretDown size={12} weight="bold" className={`transition-transform ${treasuryOpen ? "" : "-rotate-90"}`} />
                </button>
                {treasuryOpen && (
                  <div className="mt-1 space-y-0.5" data-testid="treasury-group-items">
                    {NAV.map(({ to, label, icon: Icon, testid }) => (
                      <NavLink key={to} to={to} data-testid={testid} className={navItemClass} onClick={closeMobile}>
                        <Icon size={18} weight="bold" />
                        <span>{label}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )}

          </nav>

          {/* User footer */}
          <div className="border-t border-white/10 p-3 shrink-0">
            <div className="text-[9px] tracking-[0.2em] uppercase text-gray-400 mb-0.5">User</div>
            <div className="text-[13px] font-semibold truncate text-white" data-testid="user-email">{user?.email}</div>
            <div
              className="mt-1 inline-block text-[9px] tracking-[0.2em] uppercase font-bold border border-white/20 text-gray-300 px-1.5 py-0.5 rounded"
              data-testid="user-role"
            >
              {user?.is_admin ? "ADMIN" : (user?.role || "USER")}
            </div>
            <button
              data-testid="open-oracle-profile-btn"
              onClick={() => { closeMobile(); setSetupOpen(true); }}
              className="mt-2 w-full flex items-center justify-center gap-2 bg-[#2563EB] text-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-md hover:bg-[#1D4ED8]"
            >
              <PlugsConnected size={13} weight="bold" /> Oracle Profile
            </button>
            <div className="mt-1.5 grid grid-cols-2 gap-1.5">
              <button
                data-testid="theme-toggle-btn"
                onClick={toggleTheme}
                title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                className="flex items-center justify-center gap-1.5 border border-white/20 text-gray-200 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-md hover:bg-white/[0.06]"
              >
                {theme === "dark"
                  ? <><Sun size={12} weight="bold" data-testid="theme-icon-sun" /> Light</>
                  : <><Moon size={12} weight="bold" data-testid="theme-icon-moon" /> Dark</>}
              </button>
              <button
                data-testid="logout-btn"
                onClick={async () => {
                  closeMobile();
                  // Reset theme to light on sign-out — both the visual `dark`
                  // class on <html> and the persisted localStorage value —
                  // so the login/marketing pages, and any fresh session that
                  // follows, never inherit a stale dark-mode preference.
                  forceLight();
                  await logout();
                  nav("/login");
                }}
                className="flex items-center justify-center gap-1.5 border border-white/20 text-gray-200 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-md hover:bg-white/[0.06]"
              >
                <SignOut size={12} weight="bold" /> Sign out
              </button>
            </div>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0 overflow-auto">
          {children}
        </main>
      </div>

      {/* ERP modals (global) */}
      <ProfileSetupModal />
      <AgentRunModal />
      <PurchaseReqAgentModal />
      <PurchaseOrderAgentModal />
    </div>
  );
}